/**
 * Labstep
 *
 * @module components/ProtocolValue/Form/fields
 * @desc fields for protocol value
 */

import { FieldType } from 'labstep-web/constants/form';
import type { FieldWithoutActionProps } from 'labstep-web/core/Form/Reusable/types';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { unitsAsOptions } from 'labstep-web/services/amount-unit.service';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import rules from 'labstep-web/services/validation/rules';
import pick from 'lodash/pick';

export const getProtocolValueDefaultValues = (
  protocolValue: ProtocolValue,
) => {
  const defaultValues: any = pick(protocolValue, [
    'name',
    'resource',
    'amount',
    'unit',
    'is_lineage',
  ]);

  defaultValues.type = protocolValue.inputOutputTypeOption;

  return defaultValues;
};

export const getFieldProtocolValueIsInputOutput = (
  isInput: boolean,
): FieldWithoutActionProps => {
  const options = [
    {
      label: 'Input',
      value: { is_input: true, is_output: false },
    },
    {
      label: 'Output',
      value: { is_input: false, is_output: true },
    },
  ];
  return {
    name: 'is_input_output',
    fieldLabel: 'Input/Output',
    fieldType: FieldType.ReactSelect,
    validation: rules.protocol_value.inputOutputType,
    elementProps: {
      options: options,
      defaultValue: isInput ? options[0] : options[1],
    },
  };
};

export const protocolValueIsLineage: FieldWithoutActionProps = {
  name: 'is_lineage',
  fieldLabel: 'Lineage tracking',
  fieldExplainer: `If you check this box, items specified will appear in lineage tracking.`,
  fieldType: FieldType.Checkbox,
  validation: rules.protocol_value.is_lineage,
};

export const protocolValueFieldName: FieldWithoutActionProps = {
  fieldType: FieldType.Text,
  name: 'name',
  placeholder: 'Enter field name',
  fieldLabel: 'Field Name',
  fieldExplainer: `Give the field a name that's clear and easy to refer to i.e. Sample, Forward Primer, Solvent etc.`,
  validation: rules.protocol_value.name,
};

export const protocolValueFieldAmount: FieldWithoutActionProps = {
  fieldType: FieldType.Text,
  name: 'amount',
  placeholder: 'Enter amount',
  fieldLabel: 'Amount',
  fieldExplainer: `The amount used / produced of this ${ProtocolValue.getHumanReadableEntityName()} in the ${ProtocolCollection.getHumanReadableEntityName()}.`,
  validation: rules.protocol_value.amount,
};

export const protocolValueFieldIsVariable: FieldWithoutActionProps = {
  name: 'is_variable',
  fieldLabel: 'Variable',
  fieldType: FieldType.Checkbox,
  fieldExplainer: `Enable to assign this field different values across different conditions.`,

  validation: rules.protocol_value.is_variable,
  elementProps: {
    defaultValue: true,
  },
};

export const getProtocolValueFieldUnit = (
  isChemical = false,
  additionalUnit?: string,
): FieldWithoutActionProps => ({
  name: 'unit',
  placeholder: 'Enter units',
  fieldLabel: 'Units',
  fieldExplainer: `Units for the amount specified`,
  validation: rules.protocol_value.unit,
  fieldType: FieldType.ReactSelect,
  elementProps: {
    options: unitsAsOptions(isChemical, additionalUnit),
    creatable: !isChemical,
  },
});

export const protocolValueFieldResourceCategory: FieldWithoutActionProps =
  {
    fieldType: FieldType.SearchSelect,
    name: 'resource_template',
    fieldLabel: 'Restrict to specific category (optional)',
    elementProps: {
      entityName: Resource.entityName,
      creatable: true,
      createBody: { is_template: true },
      searchKey: 'search_query',
      params: {
        is_template: true,
        serializerGroups: 'partial,paginator',
      },
    },
    validation: rules.protocol_value.resource,
    fieldExplainer: `Users will only be able to attach items of the selected category`,
  };

export const getFieldProtocolValueResource = (
  resourceCategoryId?: string | null,
): FieldWithoutActionProps => {
  return {
    fieldType: FieldType.SearchSelect,
    name: 'resource',
    elementProps: {
      entityName: 'resource',
      creatable: true,
      createBody: { template_id: resourceCategoryId },
      labelKey: 'displayName',
      params: {
        ...(resourceCategoryId && {
          template_id: resourceCategoryId,
        }),
        serializerGroups: 'partial,paginator',
      },
    },
    fieldLabel: `Restrict to specific resource (optional)`,
    fieldExplainer: `Users will only be able to attach items of the selected resource`,
    validation: rules.protocol_value.resource,
  };
};

export const getProtocolValueFormFields = ({
  resourceCategoryId,
}: {
  resourceCategoryId?: string | null;
}): FieldWithoutActionProps[] => {
  return [
    protocolValueFieldName,
    protocolValueFieldResourceCategory,
    getFieldProtocolValueResource(resourceCategoryId),
    getProtocolValueFieldUnit(),
  ];
};
